import {
  confirmUpload,
  deleteFile,
  generatePresignedUrl,
} from '@shared/api/generated/uploads/uploads'
import axios from 'axios'

export interface UploadedFileDescriptor {
  fileKey: string
  fileName: string
  contentType: string
  fileSize: number
  sha256: string
}

async function calculateSha256(file: File) {
  const content = await file.arrayBuffer()
  const digest = await crypto.subtle.digest('SHA-256', content)
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
}

function getErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : ''
}

function getErrorCode(error: unknown) {
  if (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    typeof error.code === 'string'
  ) {
    return error.code
  }

  return undefined
}

function getErrorStatus(error: unknown) {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'status' in error.response &&
    typeof error.response.status === 'number'
  ) {
    return error.response.status
  }

  return undefined
}

function getUploadFailureMessage(fileName: string, error: unknown) {
  const errorMessage = getErrorMessage(error)
  const errorCode = getErrorCode(error)
  const errorStatus = getErrorStatus(error)

  if (errorStatus === 403) {
    return `Falha ao fazer upload do arquivo ${fileName}. A URL de upload expirou ou foi rejeitada pelo bucket.`
  }

  if (errorCode === 'ERR_NETWORK' || /network error|cors/i.test(errorMessage)) {
    return `Falha ao fazer upload do arquivo ${fileName}. Verifique a conexão, o CORS ou as permissões da URL de upload no bucket R2.`
  }

  return `Falha ao fazer upload do arquivo ${fileName}.`
}

async function requestPresignedUpload(file: File) {
  const sha256 = await calculateSha256(file)
  const response = await generatePresignedUrl({
    fileName: file.name,
    contentType: file.type,
    fileSize: file.size,
  })

  const presignData = response.data

  if (!presignData?.uploadUrl || !presignData?.fileKey) {
    throw new Error(`Falha ao preparar o upload do arquivo ${file.name}.`)
  }

  return {
    uploadUrl: presignData.uploadUrl,
    fileKey: presignData.fileKey,
    fileName: file.name,
    contentType: file.type,
    fileSize: file.size,
    sha256,
  }
}

export async function uploadPendingFile(
  file: File,
  onProgress?: (percent: number) => void,
): Promise<UploadedFileDescriptor> {
  try {
    const { uploadUrl, ...uploadedFile } = await requestPresignedUpload(file)

    // Upload direto ao R2 usando uma instância limpa para evitar headers da API.
    await axios.put(uploadUrl, file, {
      headers: {
        'Content-Type': file.type,
        'If-None-Match': '*',
      },
      ...(onProgress
        ? {
            onUploadProgress: (event: { loaded: number; total?: number }) =>
              onProgress(
                Math.min(100, Math.round((event.loaded / (event.total || file.size)) * 100)),
              ),
          }
        : {}),
    })

    onProgress?.(100)
    return uploadedFile
  } catch (error) {
    // Uma recusa pode apontar para um objeto existente; uma falha de rede tem
    // resultado desconhecido. A limpeza por inventário preserva referências.
    throw new Error(getUploadFailureMessage(file.name, error), { cause: error })
  }
}

export async function uploadFile(file: File): Promise<string> {
  const uploadedFile = await uploadPendingFile(file)
  return uploadedFile.fileKey
}

export async function confirmUploadedFile(
  communicationId: string,
  uploadedFile: UploadedFileDescriptor,
) {
  await confirmUpload({
    communicationId,
    fileKey: uploadedFile.fileKey,
    fileName: uploadedFile.fileName,
    contentType: uploadedFile.contentType,
    fileSize: uploadedFile.fileSize,
    sha256: uploadedFile.sha256,
  })
}

export async function rollbackUploadedFiles(uploadedFiles: UploadedFileDescriptor[]) {
  await Promise.allSettled(uploadedFiles.map((uploadedFile) => deleteFile(uploadedFile.fileKey)))
}
